import { Links, Meta, Outlet, Scripts, ScrollRestoration, useMatches, Link } from "react-router"
import type { LinksFunction, MetaFunction } from "react-router"
import type { ReactNode } from "react"
import "./styles/tokens.css"
import "./index.css"

const FONT_CSS =
  "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap"

/**
 * Root layout — renders the document shell for every prerendered page.
 *
 * All static head tags are declared via the `links`/`meta` exports below so
 * React Router renders them identically on server and client. Do NOT add
 * hand-written <link>/<meta> elements directly inside <head>: React 19 hoists
 * head elements itself, and mixing the two causes hydration mismatches.
 *
 * The `<html lang>` attribute is derived from the deepest route match that
 * declares `handle.locale`, so every locale URL outputs its own language.
 */

export const links: LinksFunction = () => [
  { rel: "icon", type: "image/svg+xml", href: "/SCHMEITZ_DE_GROEN_tab_logo.svg" },

  // Fonts: non-render-blocking via the print-media swap trick. The stylesheet
  // is swapped to media="all" after hydration (see RootLayout effect) so the
  // DOM mutation never races hydration.
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
  { rel: "preload", as: "style", href: FONT_CSS },
  { rel: "stylesheet", href: FONT_CSS, id: "font-stylesheet", media: "print" },

  { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
]

export const meta: MetaFunction = () => [{ charSet: "utf-8" }, { viewport: "width=device-width, initial-scale=1.0" }]

function getLangFromMatches(matches: ReturnType<typeof useMatches>): string {
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i].handle as { locale?: string } | undefined
    if (handle && typeof handle === "object" && handle.locale) return handle.locale
  }
  return "nl"
}

export function Layout({ children }: { children: ReactNode }) {
  // useMatches works inside Layout because it renders within the router context.
  const matches = useMatches()
  const lang = getLangFromMatches(matches)

  return (
    <html lang={lang}>
      <head>
        <Meta />
        <Links />
      </head>
      <body>
        {/* Google tag (gtag.js) — deferred until interaction or 4s to stay out of LCP.
            Placed at the top of <body>: dataLayer must exist before tracked events fire,
            and inline scripts here avoid React 19 head-hoisting conflicts. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = gtag;
(function () {
  var scriptLoaded = false;
  function loadGtmScript() {
    if (scriptLoaded) return;
    scriptLoaded = true;
    var script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-RC077PSQ82";
    document.head.appendChild(script);
    gtag('js', new Date());
    gtag('config', 'G-RC077PSQ82', { send_page_view: false });
  }
  var events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
  function onInteract() {
    events.forEach(function (e) { window.removeEventListener(e, onInteract); });
    loadGtmScript();
  }
  events.forEach(function (e) { window.addEventListener(e, onInteract, { passive: true, once: true }); });
  setTimeout(loadGtmScript, 4000);
})();
`,
          }}
        />
        {children}
        <ScrollRestoration />
        <Scripts />
        {/* Font fallback when JS is disabled (the print-media swap never runs) */}
        <noscript>
          <link href={FONT_CSS} rel="stylesheet" />
        </noscript>
      </body>
    </html>
  )
}

export default function Root() {
  return (
    <>
      <Outlet />
    </>
  )
}

export function ErrorBoundary() {
  return (
    <>
      <main className="min-h-screen bg-site-bg font-sans text-text-primary flex flex-col items-center justify-center p-4 text-center">
        <h1 className="text-4xl font-bold mb-4">Oops!</h1>
        <p className="text-xl mb-6">Sorry, an unexpected error has occurred.</p>
        <Link to="/nl/" className="inline-block px-6 py-3 bg-text-primary text-site-bg font-bold rounded-lg hover:opacity-90 transition-opacity">
          Go back home
        </Link>
      </main>
    </>
  )
}
